import type { WhatsAppInboxMessage } from '@zrsystem/contracts';
import { ShellIconChevronDown } from '@zrsystem/ui';
import { useEffect, useRef, useState, type ReactElement } from 'react';

import { WhatsAppInboxAudioBubble } from '@/features/notification/components/whatsapp-inbox-audio-bubble.js';
import { WhatsAppInboxFileBubble } from '@/features/notification/components/whatsapp-inbox-file-bubble.js';
import { WhatsAppInboxImageBubble } from '@/features/notification/components/whatsapp-inbox-image-bubble.js';
import { WhatsAppInboxTextBubble } from '@/features/notification/components/whatsapp-inbox-text-bubble.js';
import { WhatsAppInboxVideoBubble } from '@/features/notification/components/whatsapp-inbox-video-bubble.js';
import { isWhatsAppGroupChat } from '@/features/notification/lib/whatsapp-inbox-chat-kind.js';
import { whatsAppInboxParticipantLabel } from '@/features/notification/lib/whatsapp-inbox-labels.js';
import { resolveWhatsAppInboxMediaKind } from '@/features/notification/lib/whatsapp-inbox-media-kind.js';
import { useLongPress } from '@/shared/chat/use-long-press.js';

export interface WhatsAppInboxMessageBubbleProps {
  readonly message: WhatsAppInboxMessage;
  readonly chatId: string;
  readonly edited?: boolean;
  readonly revoked?: boolean;
  readonly onEdit?: (message: WhatsAppInboxMessage) => void;
  readonly onDelete?: (message: WhatsAppInboxMessage) => void;
}

export function WhatsAppInboxMessageBubble({
  message,
  chatId,
  edited = false,
  revoked = false,
  onEdit,
  onDelete,
}: WhatsAppInboxMessageBubbleProps): ReactElement {
  const mediaKind = resolveWhatsAppInboxMediaKind(message);
  const showParticipant =
    isWhatsAppGroupChat(chatId) || message.participant !== null || message.author !== null;
  const participantLabel = showParticipant ? whatsAppInboxParticipantLabel(message) : null;

  const canAct =
    onEdit !== undefined && onDelete !== undefined && !message.id.startsWith('optimistic:');
  const canEdit =
    canAct && !revoked && message.fromMe && message.body !== null && message.body.trim().length > 0;
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  function openMenu(): void {
    const rect = bubbleRef.current?.getBoundingClientRect();
    setOpenUp(rect !== undefined && rect.bottom > window.innerHeight * 0.62);
    setMenuOpen(true);
  }
  const { pressing, ...pressHandlers } = useLongPress(openMenu, canAct && !revoked);
  const bubbleClass = [
    'whatsapp-inbox-bubble',
    message.fromMe ? 'whatsapp-inbox-bubble--own' : '',
    pressing ? 'whatsapp-inbox-bubble--holding' : '',
    menuOpen ? 'whatsapp-inbox-bubble--menu-open' : '',
  ]
    .filter((item) => item.length > 0)
    .join(' ');

  return (
    <div className={bubbleClass} ref={bubbleRef} {...pressHandlers}>
      {canAct && !revoked ? (
        <MessageMenu
          open={menuOpen}
          openUp={openUp}
          onOpen={() => {
            openMenu();
          }}
          onClose={() => {
            setMenuOpen(false);
          }}
          canEdit={canEdit}
          onEdit={() => {
            onEdit(message);
          }}
          onDelete={() => {
            onDelete(message);
          }}
        />
      ) : null}
      {participantLabel !== null ? (
        <span className="whatsapp-inbox-bubble__participant">{participantLabel}</span>
      ) : null}
      {revoked ? (
        <p className="whatsapp-inbox-bubble__deleted">Mensagem apagada</p>
      ) : (
        <>
          {mediaKind === 'image' ? <WhatsAppInboxImageBubble message={message} /> : null}
          {mediaKind === 'video' ? <WhatsAppInboxVideoBubble message={message} /> : null}
          {mediaKind === 'audio' ? <WhatsAppInboxAudioBubble message={message} /> : null}
          {mediaKind === 'file' ? <WhatsAppInboxFileBubble message={message} /> : null}
          {mediaKind === null ? <WhatsAppInboxTextBubble message={message} /> : null}
        </>
      )}
      {edited && !revoked ? <span className="whatsapp-inbox-bubble__edited">editada</span> : null}
    </div>
  );
}

function MessageMenu({
  open,
  openUp,
  onOpen,
  onClose,
  canEdit,
  onEdit,
  onDelete,
}: {
  open: boolean;
  openUp: boolean;
  onOpen: () => void;
  onClose: () => void;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): ReactElement {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const close = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (rootRef.current?.contains(target) !== true) {
        onClose();
      }
    };
    document.addEventListener('pointerdown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
    };
  }, [open, onClose]);

  return (
    <div className="whatsapp-inbox-bubble__menu" ref={rootRef} data-long-press-ignore>
      <button
        type="button"
        className="whatsapp-inbox-bubble__menu-button"
        aria-label="Ações da mensagem"
        aria-expanded={open}
        onClick={() => {
          if (open) {
            onClose();
            return;
          }
          onOpen();
        }}
      >
        <ShellIconChevronDown size={14} />
      </button>
      {open ? (
        <div
          className={
            openUp
              ? 'whatsapp-inbox-bubble__menu-panel whatsapp-inbox-bubble__menu-panel--up'
              : 'whatsapp-inbox-bubble__menu-panel'
          }
          role="menu"
        >
          {canEdit ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onClose();
                onEdit();
              }}
            >
              Editar
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onClose();
              onDelete();
            }}
          >
            Apagar
          </button>
        </div>
      ) : null}
    </div>
  );
}
